import { storageKeys } from "../config";
/**
 * The stored session of the Maker portal: `{ token, kind, operator, company,
 * expiresAt }` under "aile.maker.session" in localStorage.
 *
 * The token is issued by the Maker portal's own API, of one of two kinds:
 *
 * - `wallet`: `POST /maker-api/auth/login`, after an operator wallet of a
 *   market maker signed an EIP-4361 message;
 * - `operator`: `POST /maker-api/auth/handoff`, after an AileFX operator
 *   pressed "Maker portal ↗" in the admin console: AileFX's desk only, at most
 *   60 minutes, never past the console session. `operator` is then the AileFX
 *   operator's address, and the session holds no wallet at all
 *   (`lib/handoff.ts`).
 *
 * It is a credential for that API ONLY: `api.ts` attaches it to `/maker-api/`
 * calls and never to a gateway call, so the browser holds no gateway
 * credential and no key at all. Switching accounts in the wallet never signs
 * anyone out (`connectedAccount.ts`).
 *
 * This module is the only one that touches the stored value. It imports
 * nothing from the API layer, so `api.ts` can end a refused session here
 * without an import cycle.
 */

const STORAGE_KEY = storageKeys.session;

/** `wallet`: an operator wallet signed in; `operator`: an AileFX operator from the admin console. */
export type SessionKind = "wallet" | "operator";

export interface MakerSession {
  token: string;
  /** Read as `wallet` when absent (a session stored before the operator kind); drop that once those have expired. */
  kind: SessionKind;
  /** The signed-in operator wallet, or the AileFX operator's address: EIP-55 as the API answered it. */
  operator: string;
  company: { id: string; name: string };
  /** ISO-8601 UTC. */
  expiresAt: string;
}

/** Why a session ended: the API refused it, it expired, or the operator signed out. */
export type SessionEnd = "ended" | "expired" | "signed-out";

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const str = (value: unknown): string | null => (typeof value === "string" && value !== "" ? value : null);

function readStored(): MakerSession | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (privacy mode): treated as signed out.
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isRecord(parsed) && isRecord(parsed.company)) {
      const token = str(parsed.token);
      const operator = str(parsed.operator);
      const expiresAt = str(parsed.expiresAt);
      const id = str(parsed.company.id);
      const name = typeof parsed.company.name === "string" ? parsed.company.name : "";
      if (token !== null && operator !== null && expiresAt !== null && id !== null) {
        const kind: SessionKind = parsed.kind === "operator" ? "operator" : "wallet";
        return { token, kind, operator, expiresAt, company: { id, name } };
      }
    }
  } catch {
    // Corrupt value: fall through and self-heal by clearing it.
  }
  clearSession();
  return null;
}

export function getSession(): MakerSession | null {
  return readStored();
}

export function saveSession(session: MakerSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage unavailable: signed in only until reload.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/**
 * Whether a `storage` event (another tab of this browser wrote or cleared
 * localStorage) may have changed the stored session: a wallet sign-in, a
 * handoff from the admin console, or Sign out in that tab. `key` is null when
 * the whole storage was cleared.
 */
export function touchesStoredSession(event: Pick<StorageEvent, "key">): boolean {
  return event.key === null || event.key === STORAGE_KEY;
}

/** Bearer token of the current session, or null when signed out. */
export function getStoredToken(): string | null {
  return readStored()?.token ?? null;
}

/** True once `expiresAt` has passed (an unreadable time counts as expired). */
export function isExpired(session: MakerSession, now: number = Date.now()): boolean {
  const at = Date.parse(session.expiresAt);
  return Number.isNaN(at) || at <= now;
}

const endedListeners = new Set<(reason: SessionEnd, kind: SessionKind) => void>();

/**
 * Forgets the stored session and tells the gate, so the next screen is the
 * sign-in card (with "Your session ended", or for a session from the admin
 * console "Your session from the admin console has ended", unless the
 * operator signed out) rather than a page of refusals. Several calls can fail
 * together: only the first, while a session was still stored, notifies.
 */
export function endSession(reason: SessionEnd): void {
  const had = readStored();
  clearSession();
  if (had === null && reason !== "signed-out") return;
  for (const listener of [...endedListeners]) listener(reason, had?.kind ?? "wallet");
}

/** Called after `endSession()` dropped a session, with the kind it was. Returns the unsubscribe. */
export function onSessionEnded(listener: (reason: SessionEnd, kind: SessionKind) => void): () => void {
  endedListeners.add(listener);
  return () => {
    endedListeners.delete(listener);
  };
}

/**
 * Clears at boot the credentials older builds kept in this origin: the
 * password session ("aile.auth"), a company API key pasted into a tab, and the
 * retired demo's fake keys. This portal holds no gateway credential. Drop it
 * once no browser can still hold those keys.
 */
export function forgetLegacyCredentials(): void {
  for (const remove of [
    () => localStorage.removeItem(storageKeys.legacyAuth),
    () => localStorage.removeItem(storageKeys.legacyApiKeys),
    () => sessionStorage.removeItem(storageKeys.legacyCompanyKey),
  ]) {
    try {
      remove();
    } catch {
      // Nothing to clear if storage is unavailable.
    }
  }
}
